import React, { useState, useEffect } from "react";
import { Container, Table, Modal, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import Select from "react-select";
import { useData, US_STATES } from "../context/DataContext";
import LogoSvg from "../Assets/images/logo.svg"; // Import LogoSvg
import PaginationComponent from "../component/Pagination"; // Import PaginationComponent
import Loader from "../component/Loader";
// import styles from "../Assets/css/Batterydetail.module.css";
import styles from "../Assets/css/fleet.module.css"; // Updated CSS import

const FranklinDetails = () => {
  const { utilities: contextUtilities, fetchAllData } = useData();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [devices, setDevices] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 20; // Define how many posts per page you want
  const franklinApi = BASE_URL + "get/fwh/device/";

  // Calculate yesterday's date
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  // Date + Hour States
  const [startDate, setStartDate] = useState(getYesterdayDate());
  const [startHour, setStartHour] = useState("00:00");
  const [endDate, setEndDate] = useState(getYesterdayDate());
  const [endHour, setEndHour] = useState("23:00");
  const [dateError, setDateError] = useState("");

  // Utility and State Filters
  const [selectedUtility, setSelectedUtility] = useState("All");
  const [selectedState, setSelectedState] = useState("All");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  
  // Command parameters
  const [deviceId, setDeviceId] = useState("");
  const [touModel, setTouModel] = useState("");
  const [electricCompany, setElectricCompany] = useState("");
  const [nonWorkingDaysEnabled, setNonWorkingDaysEnabled] = useState("");
  const [switchCmd, setSwitchCmd] = useState("");
  const [runningMode, setRunningMode] = useState("");
  const [stormEn, setStormEn] = useState("");
  const [batFd, setBatFd] = useState("");
  const [solarFd, setSolarFd] = useState("");
  const [gridCh, setGridCh] = useState("");
  const [eventList, setEventList] = useState("");
  const [packageState, setPackageState] = useState("");
  const [packageArea, setPackageArea] = useState("");
  const [packageType, setPackageType] = useState("");
  const [modeChoose, setModeChoose] = useState("");
  const [defaultMode, setDefaultMode] = useState("");
  const [solarEnable, setSolarEnable] = useState("");
  const [complianceRuleType, setComplianceRuleType] = useState("");
  const [groupId, setGroupId] = useState("");
  const [setType, setSetType] = useState("");

  // Fetch device data function
  const fetchDeviceData = async (start = "", end = "", utility = "All", state = "All") => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("accessToken");

      let apiURL = franklinApi;
      const params = [];
      
      if (start && end) {
        params.push(`startdate=${start}`);
        params.push(`enddate=${end}`);
      }
      if (utility && utility !== "All") {
        params.push(`utility=${encodeURIComponent(utility)}`);
      }
      if (state && state !== "All") {
        params.push(`state=${encodeURIComponent(state)}`);
      }
      
      if (params.length > 0) {
        apiURL = `${franklinApi}?${params.join('&')}`;
      }

      const response = await fetch(apiURL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Network response was not ok " + response.statusText);
      }
      const data = await response.json();
      setDevices(data); // Assuming 'data' is the array of devices
      setFilteredData(data);
    } catch (error) {
      console.error("Error fetching device data:", error);
      setDevices([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeviceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch utilities if not already loaded in context
  useEffect(() => {
    const storedAccessToken = localStorage.getItem('accessToken');
    if (storedAccessToken && !contextUtilities) {
      fetchAllData(storedAccessToken);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------- FILTER -------- //
  const handleFilter = () => {
    if (!startDate || !endDate) {
      setDateError("Please select Start Date and End Date and Hour");
      return;
    }

    setDateError("");

    const startValue = `${startDate.replace(/-/g, '/')}T${startHour || "00:00"}:00`;
    const endValue = `${endDate.replace(/-/g, '/')}T${endHour || "23:00"}:00`;

    fetchDeviceData(startValue, endValue, selectedUtility, selectedState);
    setCurrentPage(1);
  };

  // -------- CLEAR -------- //
  const clearFilter = () => {
    setStartDate("");
    setEndDate("");
    setStartHour("");
    setEndHour("");
    setSelectedUtility("All");
    setSelectedState("All");
    setDateError("");

    fetchDeviceData();
  };

  // Calculate pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredData.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredData.length / postsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // ------------------------------
  // MODAL HANDLERS
  // ------------------------------
  const handleOpenModal = (device) => {
    setSelectedDevice(device);
    setShowModal(true);
    setDeviceId(device.id || "");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDevice(null);
    setDeviceId("");
    setTouModel("");
    setElectricCompany("");
    setNonWorkingDaysEnabled("");
    setSwitchCmd("");
    setRunningMode("");
    setStormEn("");
    setBatFd("");
    setSolarFd("");
    setGridCh("");
    setEventList("");
    setPackageState("");
    setPackageArea("");
    setPackageType("");
    setModeChoose("");
    setDefaultMode("");
    setSolarEnable("");
    setComplianceRuleType("");
    setGroupId("");
    setSetType("");
  };

  // ------------------------------
  // SEND COMMAND API
  // ------------------------------
  const sendCommand = async (commandSlug, additionalParams = {}) => {
    if (!selectedDevice) return;

    try {
      const storedAccessToken = localStorage.getItem("accessToken");
      if (!storedAccessToken) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Access token missing",
        });
        return;
      }

      const payload = {
        device_id: deviceId,
        device_type: "franklin",
        slug: commandSlug,
        ...additionalParams,
      };

      const response = await fetch(BASE_URL + "devices/send-command/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${storedAccessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to send command");
      }

      const data = await response.json();
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Command sent successfully!",
        timer: 2000,
      });
      handleCloseModal();
    } catch (error) {
      console.error("Command Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to send command. Please try again.",
      });
    }
  };

  // Command Handlers
  const handleSetTouParameters = async () => {
    if (!deviceId || !touModel || !electricCompany || !nonWorkingDaysEnabled) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Please fill all required fields",
      });
      return;
    }
    await sendCommand("set-tou-parameters", {
      deviceId,
      model: touModel,
      electricCompany,
      nonWorkingDaysEnabled,
    });
  };

  const handleBatterySetSwitchParam = async () => {
    if (!electricCompany || !switchCmd) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Please fill Electric Company and Command",
      });
      return;
    }
    await sendCommand("battery-set-switch-param", {
      electricCompany,
      cmd: switchCmd,
    });
  };

  const handleSetSmartCircuits = async () => {
    if (!electricCompany) {
      Swal.fire({
        icon: "warning",
        title: "Required Field",
        text: "Electric Company is required",
      });
      return;
    }
    await sendCommand("set-smart-circuits", { electricCompany });
  };

  const handleSetEnergyManagement = async () => {
    if (!deviceId || !runningMode || !stormEn || !batFd || !solarFd || !gridCh) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Please fill all energy management parameters",
      });
      return;
    }
    await sendCommand("set-energy-management-parameters", {
      deviceId,
      runingMode: runningMode,
      stormEn,
      batFd,
      solarFd,
      gridCh,
    });
  };

  const handleSetGridEvents = async () => {
    if (!deviceId || !eventList) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Device ID and Event List are required",
      });
      return;
    }
    await sendCommand("set-grid-events", { deviceId, eventList });
  };

  const handleSetGridPackage = async () => {
    if (!deviceId || !packageState || !packageArea || !electricCompany || !packageType || !modeChoose || !defaultMode || !solarEnable) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Please fill all grid package parameters",
      });
      return;
    }
    await sendCommand("set-grid-package", {
      deviceId,
      packageState,
      packageArea,
      eleCompany: electricCompany,
      packageType,
      modeChoose,
      defaultMode,
      solarEnable,
    });
  };

  const handleSetGridCompliance = async () => {
    if (!deviceId || !complianceRuleType) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Device ID and Compliance Rule Type are required",
      });
      return;
    }
    await sendCommand("set-grid-interconnection-compliance", {
      deviceId,
      ComplianceRuleType: complianceRuleType,
    });
  };

  const handleSetDevicesByGroup = async () => {
    if (!groupId || !setType) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Group ID and Set Type are required",
      });
      return;
    }
    await sendCommand("set-devices-by-group", { groupId, setType });
  };

  return (
    <div className={styles.fleet_view_cantaint}>
      {loading && <Loader />}

      <section className={styles.topbar}>
        <div className={styles.container}>
          <img
            className={styles.topbar_logo}
            src={LogoSvg}
            alt="Topbar Logo"
          />
        </div>
      </section>

      <section className="thermostat-details battery-details">
        <Container>
          <h2 className={styles.main_title}>Franklin Details</h2>

          {/* FILTER UI */}
          <div className="row g-2 mt-3 mb-2 align-items-end">
            {/* Start Date */}
            <div className="col-6 col-md-3">
              <label className="small">Start Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            {/* Start Hour */}
            <div className="col-6 col-md-2">
              <label className="small">Start Hour</label>
              <select
                className="form-control form-control-sm"
                value={startHour}
                onChange={(e) => setStartHour(e.target.value)}
              >
                <option value="">Hr</option>
                {Array.from({ length: 24 }).map((_, i) => {
                  const hr = i.toString().padStart(2, "0");
                  return (
                    <option key={i} value={`${hr}:00`}>
                      {hr}:00
                    </option>
                  );
                })}
              </select>
            </div>

            {/* End Date */}
            <div className="col-6 col-md-3">
              <label className="small">End Date</label>
              <input
                type="date"
                min={startDate}
                className="form-control form-control-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            {/* End Hour */}
            <div className="col-6 col-md-2">
              <label className="small">End Hour</label>
              <select
                className="form-control form-control-sm"
                value={endHour}
                onChange={(e) => setEndHour(e.target.value)}
              >
                <option value="">Hr</option>
                {Array.from({ length: 24 }).map((_, i) => {
                  const hr = i.toString().padStart(2, "0");
                  return (
                    <option key={i} value={`${hr}:00`}>
                      {hr}:00
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Utility Filter */}
            {/* <div className="col-6 col-md-4">
              <label className="small">Utility</label>
              <Select
                options={[
                  { value: "All", label: "All Utility" },
                  ...(contextUtilities || []).map((utility) => ({
                    value: utility.id.toString(),
                    label: utility.utility_name,
                  })),
                ]}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: "#fff",
                    borderColor: "#b6b6b6",
                    minHeight: "31px",
                    height: "31px",
                    boxShadow: "none",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#4c2806",
                    borderRadius: "3px",
                    cursor: "pointer"
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                    padding: "0 6px",
                    display: "flex",
                    alignItems: "center"
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: "#4c2806",
                    position: "relative",
                    top: "auto",
                    transform: "none",
                    margin: 0,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: "0px",
                    padding: "0px"
                  }),
                  indicatorSeparator: () => ({
                    display: "none",
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    fontSize: "13px",
                    color: "#4c2806"
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#ffe0b2" : state.isFocused ? "#fff7e9" : "white",
                    color: "#4c2806",
                    cursor: "pointer"
                  })
                }}
                value={
                  selectedUtility === "All"
                    ? { value: "All", label: "All Utility" }
                    : (contextUtilities || []).map(u => ({ value: u.id.toString(), label: u.utility_name })).find(opt => opt.value === selectedUtility)
                }
                onChange={(option) => setSelectedUtility(option ? option.value : "All")}
                placeholder="Select Utility"
                isSearchable
                isClearable
              />
            </div> */}

            {/* State Filter */}
            {/* <div className="col-6 col-md-4">
              <label className="small">State</label>
              <Select
                options={[
                  { value: "All", label: "All States" },
                  ...US_STATES.map((state) => ({ value: state, label: state })),
                ]}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: "#fff",
                    borderColor: "#b6b6b6",
                    minHeight: "31px",
                    height: "31px",
                    boxShadow: "none",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#4c2806",
                    borderRadius: "3px",
                    cursor: "pointer"
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                    padding: "0 6px",
                    display: "flex",
                    alignItems: "center"
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: "#4c2806",
                    position: "relative",
                    top: "auto",
                    transform: "none",
                    margin: 0,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: "0px",
                    padding: "0px"
                  }),
                  indicatorSeparator: () => ({
                    display: "none",
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    fontSize: "13px",
                    color: "#4c2806"
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#ffe0b2" : state.isFocused ? "#fff7e9" : "white",
                    color: "#4c2806",
                    cursor: "pointer"
                  })
                }}
                value={
                  selectedState === "All"
                    ? { value: "All", label: "All States" }
                    : { value: selectedState, label: selectedState }
                }
                onChange={(option) => setSelectedState(option ? option.value : "All")}
                placeholder="Select State"
                isSearchable
                isClearable
              />
            </div> */}

            {/* Buttons */}
            <div className="col-12 col-md-2 d-flex gap-2 mt-md-0 mt-2">
              <button
                className="btn btn-sm filter-button"
                style={{ backgroundColor: "#ff5900", color: "white", border: "none" }}
                onClick={handleFilter}
              >
                Filter
              </button>
              <button
                className="btn btn-secondary btn-sm filter-button"
                onClick={clearFilter}
              >
                Clear
              </button>
            </div>
          </div>

          {dateError && <div style={{ color: "red" }}>{dateError}</div>}

          <div
            className="table-responsive mt-4"
          >
            <Table className={styles.table}>
              <thead>
                <tr>
                  <th>Device ID</th>
                  {/* <th className="text-center">Device Type</th> */}
                  <th className="text-center">Device ZIP</th>
                  <th className="text-center">aGate</th>
                  <th className="text-center">aPower</th>
                  {/* <th className="text-center">Action</th> */}
                </tr>
              </thead>
              <tbody>
                {currentPosts.length > 0 ? (
                  currentPosts.map((device) => (
                    <tr key={device.deviceID}>
                      <td>{device.id}</td>
                      {/* <td className="text-center">Franklin</td> */}
                      <td className="text-center">{device.zip_code}</td>
                      <td className="text-center">{device.agate}</td>
                      <td className="text-center">{device.apower}</td>
                      {/* <td className="text-center">
                        <button
                          className="btn btn-sm"
                          style={{ backgroundColor: "#ff5900", color: "white", border: "none" }}
                          onClick={() => handleOpenModal(device)}
                        >
                          Commands
                        </button>
                      </td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Container>
      </section>

      {/* -------------------- COMMANDS MODAL -------------------- */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Franklin Device Commands</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDevice && (
            <div>
              <p>
                <strong>Device ID:</strong> {selectedDevice.id}
              </p>
              <p>
                <strong>ZIP Code:</strong> {selectedDevice.zip_code}
              </p>

              {/* Device Information */}
              <h6 className="mt-4 mb-3">Device Information</h6>
              <div className="mb-3">
                <label className="form-label">
                  Device ID <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={deviceId}
                  readOnly
                  disabled
                  placeholder="Device ID"
                />
              </div>

              {/* TOU Parameters */}
              <h6 className="mt-4 mb-3">Time-of-Use (TOU) Parameters</h6>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label small">TOU Model <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={touModel}
                    onChange={(e) => setTouModel(e.target.value)}
                    placeholder="TOU Model"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">Electric Company <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={electricCompany}
                    onChange={(e) => setElectricCompany(e.target.value)}
                    placeholder="Electric Company Code"
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label small">Non-Working Days Enabled <span style={{ color: "red" }}>*</span></label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={nonWorkingDaysEnabled}
                  onChange={(e) => setNonWorkingDaysEnabled(e.target.value)}
                  placeholder="true/false"
                />
              </div>
              <Button variant="primary" className="w-100 mb-3" onClick={handleSetTouParameters}>
                Set TOU Parameters
              </Button>

              {/* Battery Switch Parameters */}
              <h6 className="mt-4 mb-3">Battery Switch Parameters</h6>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label small">Electric Company <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={electricCompany}
                    onChange={(e) => setElectricCompany(e.target.value)}
                    placeholder="Electric Company Code"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">Switch Command <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={switchCmd}
                    onChange={(e) => setSwitchCmd(e.target.value)}
                    placeholder="Command"
                  />
                </div>
              </div>
              <Button variant="secondary" className="w-100 mb-3" onClick={handleBatterySetSwitchParam}>
                Set Battery Switch
              </Button>

              {/* Smart Circuits */}
              <h6 className="mt-4 mb-3">Smart Circuits</h6>
              <div className="mb-3">
                <label className="form-label small">Electric Company <span style={{ color: "red" }}>*</span></label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={electricCompany}
                  onChange={(e) => setElectricCompany(e.target.value)}
                  placeholder="Electric Company Code"
                />
              </div>
              <Button variant="info" className="w-100 mb-3" onClick={handleSetSmartCircuits}>
                Set Smart Circuits
              </Button>

              {/* Energy Management Parameters */}
              <h6 className="mt-4 mb-3">Energy Management Parameters</h6>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label small">Running Mode <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={runningMode}
                    onChange={(e) => setRunningMode(e.target.value)}
                    placeholder="Mode"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">Storm Enable <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={stormEn}
                    onChange={(e) => setStormEn(e.target.value)}
                    placeholder="true/false"
                  />
                </div>
              </div>
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <label className="form-label small">Battery Feed <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={batFd}
                    onChange={(e) => setBatFd(e.target.value)}
                    placeholder="Value"
                  />
                </div>
                <div className="col-4">
                  <label className="form-label small">Solar Feed <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={solarFd}
                    onChange={(e) => setSolarFd(e.target.value)}
                    placeholder="Value"
                  />
                </div>
                <div className="col-4">
                  <label className="form-label small">Grid Charge <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={gridCh}
                    onChange={(e) => setGridCh(e.target.value)}
                    placeholder="Value"
                  />
                </div>
              </div>
              <Button variant="success" className="w-100 mb-3" onClick={handleSetEnergyManagement}>
                Set Energy Management
              </Button>

              {/* Grid Events */}
              <h6 className="mt-4 mb-3">Grid Events</h6>
              <div className="mb-3">
                <label className="form-label small">Event List (JSON) <span style={{ color: "red" }}>*</span></label>
                <textarea
                  className="form-control form-control-sm"
                  rows="3"
                  value={eventList}
                  onChange={(e) => setEventList(e.target.value)}
                  placeholder='{"events": []}'
                />
              </div>
              <Button variant="warning" className="w-100 mb-3" onClick={handleSetGridEvents}>
                Set Grid Events
              </Button>

              {/* Grid Package */}
              <h6 className="mt-4 mb-3">Grid Package</h6>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label small">Package State <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={packageState}
                    onChange={(e) => setPackageState(e.target.value)}
                    placeholder="State"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">Package Area <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={packageArea}
                    onChange={(e) => setPackageArea(e.target.value)}
                    placeholder="Area"
                  />
                </div>
              </div>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label small">Package Type <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={packageType}
                    onChange={(e) => setPackageType(e.target.value)}
                    placeholder="Type"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">Mode Choose <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={modeChoose}
                    onChange={(e) => setModeChoose(e.target.value)}
                    placeholder="Mode"
                  />
                </div>
              </div>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label small">Default Mode <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={defaultMode}
                    onChange={(e) => setDefaultMode(e.target.value)}
                    placeholder="Default"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">Solar Enable <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={solarEnable}
                    onChange={(e) => setSolarEnable(e.target.value)}
                    placeholder="true/false"
                  />
                </div>
              </div>
              <Button variant="primary" className="w-100 mb-3" onClick={handleSetGridPackage}>
                Set Grid Package
              </Button>

              {/* Grid Interconnection Compliance */}
              <h6 className="mt-4 mb-3">Grid Interconnection Compliance</h6>
              <div className="mb-3">
                <label className="form-label small">Compliance Rule Type <span style={{ color: "red" }}>*</span></label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={complianceRuleType}
                  onChange={(e) => setComplianceRuleType(e.target.value)}
                  placeholder="Rule Type"
                />
              </div>
              <Button variant="danger" className="w-100 mb-3" onClick={handleSetGridCompliance}>
                Set Grid Compliance
              </Button>

              {/* Set Devices by Group */}
              <h6 className="mt-4 mb-3">Set Devices by Group</h6>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label small">Group ID <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={groupId}
                    onChange={(e) => setGroupId(e.target.value)}
                    placeholder="Group ID"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">Set Type <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={setType}
                    onChange={(e) => setSetType(e.target.value)}
                    placeholder="Type"
                  />
                </div>
              </div>
              <Button variant="secondary" className="w-100 mb-3" onClick={handleSetDevicesByGroup}>
                Set Devices by Group
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FranklinDetails;
